import type { FormEvent, KeyboardEvent } from 'react'
import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DIALOG_FIELD_CLASS,
  DialogField,
  DialogFold,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useInviteUsersMutation } from '@/hooks/mutations/useOrganizationInvitesMutations'
import { useAccountType } from '@/hooks/useAccountType'
import { useProjectMultiPicker } from '@/hooks/useRecordPicker'
import { useSubscriptionSeats } from '@/hooks/useSubscriptionSeats'
import { getErrorMessage, getErrorStatus } from '@/lib/api/error-utils'
import { permissionsApi } from '@/lib/api/permissions.api'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import { useOrganizationStore } from '@/store/organization.store'
import { getModifierKeyLabel } from '@/utils/keyboard-utils'
import {
  CheckIcon,
  CircleNotchIcon,
  EnvelopeIcon,
  ShoppingCartIcon,
  UserPlusIcon,
  UsersIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react'
import { AnimatePresence, m as motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { PermissionSet } from '@scrambl/shared'

const logger = createLogger('components/layout/InviteTeamMembersDialog')

export function InviteTeamMembersDialog({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}) {
  const navigate = useNavigate()
  const [emails, setEmails] = useState('')
  const [permissionSetId, setPermissionSetId] = useState<string>('')
  // Personal accounts get a simplified access picker in place of the business
  // permission-set matrix — "Member" (MEMBER role), "Can manage" (admin-level
  // role), or "Limited" (MEMBER role + the Limited Member permission set, a
  // kid-friendly preset). All three map onto the same invite payload.
  const [personalRole, setPersonalRole] = useState<
    'MEMBER' | 'ADMIN' | 'LIMITED'
  >('MEMBER')
  const [permissionSets, setPermissionSets] = useState<PermissionSet[]>([])
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(
    new Set(),
  )
  // Project access is the heavy optional field, so it folds until asked for.
  const [isProjectAccessOpen, setIsProjectAccessOpen] = useState(false)

  const { currentOrganization, isOrgAdmin } = useOrganizationStore()
  const { isPersonal } = useAccountType()
  const inviteUsersMutation = useInviteUsersMutation()
  const { seatUsage, availableSeats, fetchSeatUsage, subscription } =
    useSubscriptionSeats()
  // Projects are searched on the server while the dialog is open; selected
  // ones off the current page are resolved by id and pinned to the list.
  const projectPicker = useProjectMultiPicker({
    selectedIds: Array.from(selectedProjects),
    enabled: isOpen,
  })
  const projects = projectPicker.options
  const isLoadingProjectsQuery =
    projectPicker.isSearching && projects.length === 0

  // Check if current user can set member roles (admin or owner)
  const canSetRoles = isOrgAdmin()

  // Load permission sets and seat usage when dialog opens
  useEffect(() => {
    if (isOpen && currentOrganization?.id) {
      // Fetch seat usage — business only. Personal accounts have no subscription
      // or seats, so there is nothing to fetch.
      if (!isPersonal) {
        fetchSeatUsage()
      }

      // Load permission sets if user can set roles. Business accounts use the
      // full permission-set picker; personal accounts only need the sets loaded
      // so the "Limited" choice can resolve the Limited Member set by systemKey
      // (no picker UI — the role dropdown drives it).
      if (canSetRoles) {
        setIsLoadingPermissions(true)
        permissionsApi
          .getPermissionSets(currentOrganization.id)
          .then(sets => {
            setPermissionSets(sets.filter(set => set.isActive))
            // Default to Basic Access if available
            const basicAccess = sets.find(
              set => set.systemKey === 'basic_access',
            )
            if (basicAccess) {
              setPermissionSetId(basicAccess.id)
            }
          })
          .catch(error => {
            logger.error('Failed to load permission sets:', error)
            toast.error('Failed to load permission sets')
          })
          .finally(() => {
            setIsLoadingPermissions(false)
          })
      }
    }
  }, [isOpen, currentOrganization?.id, canSetRoles, isPersonal, fetchSeatUsage])

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setEmails('')
      setSelectedProjects(new Set())
      setErrors({})
      setIsProjectAccessOpen(false)
    }
  }, [isOpen])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    // Validate emails
    if (!emails.trim()) {
      newErrors.emails = 'Please enter at least one email address'
    } else {
      const emailList = emails.split(',').map(email => email.trim())
      const invalidEmails = emailList.filter(email => !validateEmail(email))

      if (invalidEmails.length > 0) {
        newErrors.emails = `Invalid email format: ${invalidEmails.join(', ')}`
      }

      // Check if there are enough seats available
      if (subscription && availableSeats !== undefined) {
        if (availableSeats < 0) {
          newErrors.emails = `You are ${Math.abs(availableSeats)} seats over capacity. Please purchase more seats or suspend members before inviting.`
        } else if (emailList.length > availableSeats) {
          newErrors.emails = `You only have ${availableSeats} seat${availableSeats === 1 ? '' : 's'} available. Please purchase more seats or invite fewer members.`
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email)
  }

  const handleManageSeats = () => {
    onClose()
    navigate(
      `/${currentOrganization?.slug}/settings/administration/subscription`,
    )
  }

  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  // "All" means every project currently listed (the page plus pinned picks).
  const listedProjectIds = projects.map(p => p.value)
  const isEveryListedSelected =
    listedProjectIds.length > 0 &&
    listedProjectIds.every(id => selectedProjects.has(id))
  const toggleAllProjects = () => {
    if (isEveryListedSelected) {
      setSelectedProjects(prev => {
        const next = new Set(prev)
        listedProjectIds.forEach(id => next.delete(id))
        return next
      })
    } else {
      setSelectedProjects(prev => new Set([...prev, ...listedProjectIds]))
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      void handleSubmit(e as unknown as FormEvent)
    }
  }

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault()
    if (!validateForm() || !currentOrganization?.id) return

    // Double-check seat availability
    if (subscription && availableSeats <= 0) {
      toast.error(
        availableSeats < 0
          ? `You are ${Math.abs(availableSeats)} seats over capacity. Please purchase additional seats first.`
          : 'No seats available. Please purchase additional seats first.',
      )
      return
    }

    setIsSubmitting(true)
    try {
      const emailList = emails.split(',').map(email => email.trim())

      // Personal accounts pick the role via the simplified picker; business
      // accounts always invite as MEMBER + a permission set. The "Limited"
      // personal choice invites as MEMBER with the Limited Member set attached.
      const limitedMemberSet = permissionSets.find(
        set => set.systemKey === 'limited_member',
      )
      const finalRole: 'MEMBER' | 'ADMIN' = isPersonal
        ? personalRole === 'ADMIN'
          ? 'ADMIN'
          : 'MEMBER'
        : 'MEMBER'
      const finalPermissionSetId = isPersonal
        ? personalRole === 'LIMITED'
          ? limitedMemberSet?.id
          : undefined
        : canSetRoles && permissionSetId
          ? permissionSetId
          : undefined

      // First, invite users to the organization with project metadata
      await inviteUsersMutation.mutateAsync({
        organizationId: currentOrganization.id,
        invites: emailList.map(email => ({
          email,
          role: finalRole,
          permissionSetId: finalPermissionSetId,
          metadata:
            selectedProjects.size > 0
              ? { projectIds: Array.from(selectedProjects) }
              : undefined,
        })),
      })

      // Reset form
      setEmails('')
      setSelectedProjects(new Set())
      // Reset permission set to default
      const basicAccess = permissionSets.find(
        set => set.systemKey === 'basic_access',
      )
      if (basicAccess) {
        setPermissionSetId(basicAccess.id)
      }

      // Refresh seat usage (invites invalidation handled by mutation hook)
      try {
        await fetchSeatUsage()
      } catch (refreshError) {
        logger.warn('Failed to refresh seat usage after sending:', refreshError)
      }

      // Close dialog
      onClose()

      // Trigger success callback
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      logger.error('Failed to send invitations:', error)

      const fallbackMessage = 'Failed to send invitations'
      const status = getErrorStatus(error)
      const apiMessage = getErrorMessage(error, fallbackMessage)
      let errorMessage = fallbackMessage

      if (status === 500) {
        errorMessage =
          'The invitation service is temporarily unavailable. Please try again later.'
      } else if (status === 401) {
        errorMessage =
          "You don't have permission to send invitations. Please contact your administrator."
      } else if (status === 403) {
        errorMessage =
          'Access denied. Please check your permissions or contact your administrator.'
      } else if (status === 400) {
        errorMessage =
          apiMessage !== fallbackMessage
            ? apiMessage
            : 'Invalid invitation data. Please check the email addresses and try again.'
      } else {
        errorMessage = apiMessage
      }

      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Calculate how many emails are being entered
  const emailCount = emails.trim()
    ? emails.split(',').filter(e => e.trim()).length
    : 0
  const hasEnoughSeats = subscription ? emailCount <= availableSeats : true
  // PERSONAL accounts are free (no subscription, no seats) — seat capping never
  // applies, so the "no seats / purchase more" block must never gate the
  // personal member-invite flow.
  const hasNoSeats =
    !isPersonal && subscription !== undefined && availableSeats <= 0

  const footer = (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        size="sm"
        disabled={
          isSubmitting ||
          (canSetRoles && !isPersonal && isLoadingPermissions) ||
          !hasEnoughSeats ||
          hasNoSeats
        }
        className="min-w-[100px]"
        title={`${getModifierKeyLabel()}+Enter`}
      >
        {isSubmitting ? (
          <>
            <CircleNotchIcon className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <EnvelopeIcon className="mr-1.5 h-3.5 w-3.5" />
            Send Invites
          </>
        )}
      </Button>
    </>
  )

  return (
    <DialogPattern
      open={isOpen}
      onOpenChange={onClose}
      title={isPersonal ? 'Invite Members' : 'Invite Team Members'}
      description={
        isPersonal
          ? 'Share your space with the people you live and plan with'
          : undefined
      }
      icon={<UserPlusIcon className="h-4 w-4 text-primary" />}
      isForm
      formProps={{ onSubmit: handleSubmit }}
      contentClassName="max-h-[60vh] space-y-4"
      footer={footer}
    >
      <>
        {/* Seat availability — business only. Personal accounts are free (no
            subscription) so seat/billing language never applies. */}
        {!isPersonal && subscription && seatUsage && (
          <div
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2.5 text-xs',
              availableSeats <= 0
                ? 'bg-destructive/10 text-destructive'
                : 'bg-muted/30',
            )}
          >
            <UsersIcon className="h-4 w-4 shrink-0" />
            <span className="flex-1">
              {availableSeats < 0 ? (
                <>
                  <strong>{Math.abs(availableSeats)}</strong> seats over
                  capacity
                  <span className="ml-1 text-muted-foreground">
                    ({seatUsage.usedSeats} used of {seatUsage.totalSeats})
                  </span>
                </>
              ) : (
                <>
                  <strong>{availableSeats}</strong> of{' '}
                  <strong>{seatUsage.totalSeats}</strong> seats available
                  {emailCount > 0 && (
                    <span className="ml-1.5 text-muted-foreground">
                      • Inviting {emailCount}
                    </span>
                  )}
                </>
              )}
            </span>
            {availableSeats < 3 && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={handleManageSeats}
                className="h-auto shrink-0 p-0 text-xs"
              >
                <ShoppingCartIcon className="mr-1 h-3 w-3" />
                Add seats
              </Button>
            )}
          </div>
        )}

        {/* No-seats warning (shown in place of the form when capped) */}
        {hasNoSeats && (
          <div className="flex items-start gap-3 rounded-md bg-destructive/10 px-3 py-3 text-xs text-destructive">
            <WarningCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1">
              <p className="font-medium">
                {availableSeats < 0
                  ? `You are ${Math.abs(availableSeats)} seats over capacity.`
                  : 'No seats available.'}
              </p>
              <p className="text-muted-foreground">
                Purchase more seats{' '}
                {availableSeats < 0 ? 'or suspend members ' : ''}to invite new
                members.
              </p>
            </div>
          </div>
        )}

        {!hasNoSeats && (
          <DialogField
            label="Email addresses"
            required
            labelAction={
              emailCount > 0 ? (
                <Badge
                  variant={hasEnoughSeats ? 'secondary' : 'destructive'}
                  className="px-1.5 py-0 text-[10px]"
                >
                  {emailCount}
                </Badge>
              ) : undefined
            }
          >
            <Input
              type="text"
              value={emails}
              onChange={e => setEmails(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="team@company.com, john@company.com"
              autoFocus
              disabled={isSubmitting}
              aria-invalid={Boolean(errors.emails) || undefined}
              className={cn(
                DIALOG_FIELD_CLASS,
                'placeholder:text-muted-foreground/50',
                errors.emails && 'bg-destructive/10 focus:bg-destructive/10',
              )}
            />
            <AnimatePresence>
              {errors.emails ? (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1 text-xs text-destructive"
                >
                  <WarningCircleIcon className="h-3 w-3" />
                  {errors.emails}
                </motion.p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Separate multiple emails with commas
                </p>
              )}
            </AnimatePresence>
          </DialogField>
        )}

        {/* Access — personal accounts get a friendly picker ("Member" /
            "Can manage" / "Limited") in place of the business permission-set
            matrix. All three map onto the existing member roles. */}
        {!hasNoSeats && canSetRoles && isPersonal && (
          <DialogField
            label="Access"
            hint={
              personalRole === 'ADMIN'
                ? 'Can manage your space, people, and everyone’s access'
                : personalRole === 'LIMITED'
                  ? 'Great for kids — sees shared plans, manages their own tasks'
                  : 'Can view and work on shared projects and tasks'
            }
          >
            <Select
              value={personalRole}
              onValueChange={value =>
                setPersonalRole(value as 'MEMBER' | 'ADMIN' | 'LIMITED')
              }
              disabled={isSubmitting}
            >
              <SelectTrigger className={cn(DIALOG_FIELD_CLASS, 'text-sm')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MEMBER" className="text-sm">
                  Member
                </SelectItem>
                <SelectItem value="ADMIN" className="text-sm">
                  Can manage
                </SelectItem>
                <SelectItem value="LIMITED" className="text-sm">
                  Limited
                </SelectItem>
              </SelectContent>
            </Select>
          </DialogField>
        )}

        {/* Access level (business) */}
        {!hasNoSeats && canSetRoles && !isPersonal && (
          <DialogField
            label="Access level"
            hint={
              permissionSetId
                ? permissionSets.find(s => s.id === permissionSetId)
                    ?.description
                : undefined
            }
          >
            <Select
              value={permissionSetId}
              onValueChange={setPermissionSetId}
              disabled={isLoadingPermissions || isSubmitting}
            >
              <SelectTrigger className={cn(DIALOG_FIELD_CLASS, 'text-sm')}>
                <SelectValue
                  placeholder={
                    isLoadingPermissions ? 'Loading...' : 'Choose access level'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="text-xs">
                    Available Levels
                  </SelectLabel>
                  {permissionSets.map(set => (
                    <SelectItem key={set.id} value={set.id} className="text-sm">
                      {set.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </DialogField>
        )}

        {/* Project access: a search box plus a scrolling list, and most
            invites do not set it, so it stays folded until asked for. */}
        {!hasNoSeats && (
          <DialogFold
            label="Add project access"
            open={isProjectAccessOpen}
            onOpenChange={setIsProjectAccessOpen}
            hasValue={selectedProjects.size > 0}
            disabled={isSubmitting}
          >
            <DialogField
              label="Project access"
              hint={
                isPersonal
                  ? 'They’ll be able to see and help with the selected projects'
                  : 'Users will be added as contributors to selected projects'
              }
              labelAction={
                <div className="flex items-center gap-2">
                  {selectedProjects.size > 0 && (
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0 text-[10px]"
                    >
                      {selectedProjects.size}
                    </Badge>
                  )}
                  {projects.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleAllProjects}
                      className="text-xs text-primary hover:underline"
                      disabled={isSubmitting}
                    >
                      {isEveryListedSelected ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>
              }
            >
              <Input
                value={projectPicker.search}
                onChange={event => projectPicker.setSearch(event.target.value)}
                placeholder="Search projects, codes, or clients..."
                className={cn(DIALOG_FIELD_CLASS, 'text-xs')}
                disabled={isSubmitting}
              />
              {isLoadingProjectsQuery ? (
                <div className="flex items-center justify-center py-6">
                  <CircleNotchIcon className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : projects.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  {projectPicker.search
                    ? 'No projects match'
                    : 'No projects available'}
                </div>
              ) : (
                <div className="h-[140px] overflow-y-auto rounded-md bg-muted/30">
                  <div className="space-y-0.5 p-2">
                    {projects.map(project => {
                      const isSelected = selectedProjects.has(project.value)
                      return (
                        <label
                          key={project.value}
                          htmlFor={`project-${project.value}`}
                          className={cn(
                            'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors',
                            'hover:bg-muted/50',
                            isSelected && 'bg-primary/5',
                            isSubmitting && 'pointer-events-none opacity-50',
                          )}
                        >
                          <input
                            type="checkbox"
                            id={`project-${project.value}`}
                            checked={isSelected}
                            onChange={() =>
                              toggleProjectSelection(project.value)
                            }
                            disabled={isSubmitting}
                            className="h-3.5 w-3.5 rounded border-input accent-primary"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium">
                              {project.label}
                            </p>
                            {project.description && (
                              <p className="truncate text-[11px] text-muted-foreground">
                                {project.description}
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <CheckIcon className="h-3.5 w-3.5 shrink-0 text-primary" />
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </DialogField>
          </DialogFold>
        )}
      </>
    </DialogPattern>
  )
}
